import { yupResolver } from "@hookform/resolvers/yup";
import { getRegisteredCountries } from "@kiss-solutions/countries";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { loadUserReference, saveUserReference } from "../../api/localAuthPersistence";
import { registerUser } from "../../api/signupApi";
import { i18_SIGNUP } from "../../i18nReferences";
import { generateSecretFromObject } from "../../utils/cryptoUtils";
import { createSupportSchema } from "../../models/support";
import { createNewUser, createUserSchema } from "./signupModel";
import i18n from "../../i18n";
import { useCreateSupportTicket } from "./useCreateSupportTicket";
import { useStudioNameAvailability } from "./useStudioNameAvailability";
import SignupErrorView from "./SignupErrorView";
import SignupSupportSuccessView from "./SignupSupportSuccessView";
import SignupWarningView from "./SignupWarningView";
import SignupFormView from "./SignupFormView";
import { getAffiliateReferrer } from "../../utils/affiliateReferrer";

const DEV_SUCCESS_URL = "https://kisscal.tattoo/test-abo-erfolgreich-staging/";
const PROD_SUCCESS_URL = "https://kisscal.tattoo/test-abo-erfolgreich/";

// View states for the signup component
const VIEW_STATE = {
    WARNING: "warning",
    SIGNUP_FORM: "signup_form",
    REGISTRATION_ERROR: "registration_error",
    SUPPORT_SUCCESS: "support_success",
};

const Signup = () => {
    const affiliate = useMemo(() => {
        if (typeof window === "undefined") {
            return getAffiliateReferrer();
        }

        const searchParams = new URLSearchParams(window.location.search);
        return searchParams.get("affiliate") || getAffiliateReferrer();
    }, []);
    const { t } = useTranslation([i18_SIGNUP]);
    const schema = createUserSchema();
    const navigate = useNavigate();
    const countryMap = useMemo(() => getRegisteredCountries(), []);

    // View state management
    const [registrationErrorType, setRegistrationErrorType] = useState(null);

    // Check if user already has an account
    const userReference = useMemo(() => loadUserReference(), []);
    const hasExistingAccount = useMemo(() => Boolean(userReference?.userId), [userReference]);

    // Form for signup
    const signupForm = useForm({
        mode: "onTouched",
        resolver: yupResolver(schema),
        defaultValues: createNewUser(),
    });

    const { studioNameInUseError, handleStudioNameBlur, markStudioNameTaken } = useStudioNameAvailability(signupForm);

    const registerMutation = useMutation({
        mutationFn: registerUser,
        onSuccess: () => {
            saveUserReference({userId: 0});
            window.location.href = import.meta.env.REACT_APP_ENV === "production" ? PROD_SUCCESS_URL : DEV_SUCCESS_URL;
        },
        onError: (error, accountData) => {
            if (error?.response?.status === 409) {
                // Name taken: keep the form open and show the field error; no account was created.
                markStudioNameTaken(accountData.studioName);
                return;
            }
            saveUserReference({userId: 0});
            if (error?.response?.status === 429) {
                setRegistrationErrorType("MULTIPLE_REQUEST_ERROR.INFO");
            } else {
                setRegistrationErrorType("USER_SETUP_ERROR.INFO");
            }
        },
    });

    // Support ticket management for warning view
    const supportTicketMutation = useCreateSupportTicket();

    // Derive view state from data states (no useState needed)
    const viewState = useMemo(() => {
        if (supportTicketMutation.isSuccess) {
            return VIEW_STATE.SUPPORT_SUCCESS;
        }
        if (registrationErrorType) {
            return VIEW_STATE.REGISTRATION_ERROR;
        }
        if (hasExistingAccount) {
            return VIEW_STATE.WARNING;
        }
        return VIEW_STATE.SIGNUP_FORM;
    }, [hasExistingAccount, registrationErrorType, supportTicketMutation.isSuccess]);

    // Form for support ticket (used in warning view)
    const supportSchema = createSupportSchema();
    const defaultSubject = useMemo(() => {
        const baseSubject = t("WARNING.DEFAULT_SUBJECT");
        if (userReference?.userId) {
            return `${baseSubject} - User ID: ${userReference.userId}`;
        }
        return baseSubject;
    }, [t, userReference]);

    const supportForm = useForm({
        mode: "onTouched",
        resolver: yupResolver(supportSchema),
        defaultValues: {
            type: "SUPPORT_REQUEST",
            subject: defaultSubject,
            description: "",
            email: "",
            phone: "",
        },
    });

    const countrySelection = useMemo(
        () =>
            (countryMap &&
                Object.values(countryMap).map((c) => ({
                    id: c.country_code,
                    text: c.name,
                }))) ||
            [],
        [countryMap]
    );

    const countryCode = useWatch({ control: signupForm.control, name: "countryCode" });
    const studioLocale = useWatch({ control: signupForm.control, name: "studioLocale" });

    const stateSelection = useMemo(
        () =>
            countryCode
                ? countryMap[countryCode]?.subregions.map((r) => ({
                      id: r.code,
                      text: r.subdivision_name,
                  })) || []
                : [],
        [countryCode, countryMap]
    );

    const handleCountryChanged = useCallback(() => {
        signupForm.setValue("subregionId", "");
    }, [signupForm]);

    // Update depositName when studioLocale changes. getFixedT does not load a language that differs from the UI
    // language; without loading it first, the default deposit name falls back to English.
    // loadLanguages resolves immediately for a language whose request is still pending (e.g. fr -> it -> fr),
    // so the name is set only once the "loaded" event reports the studio language as settled.
    useEffect(() => {
        if (!studioLocale) {
            return undefined;
        }
        let isPending = true;
        const setDefaultDepositNameOnceLoaded = () => {
            if (!isPending || !i18n.hasLoadedNamespace(i18_SIGNUP, { lng: studioLocale })) {
                return;
            }
            isPending = false;
            i18n.off("loaded", setDefaultDepositNameOnceLoaded);
            const fixedT = i18n.getFixedT(studioLocale, i18_SIGNUP);
            signupForm.setValue("depositName", fixedT("DEFAULT.DEPOSIT_NAME"));
        };
        i18n.on("loaded", setDefaultDepositNameOnceLoaded);
        i18n.loadLanguages(studioLocale).then(setDefaultDepositNameOnceLoaded);
        return () => {
            isPending = false;
            i18n.off("loaded", setDefaultDepositNameOnceLoaded);
        };
    }, [studioLocale, signupForm]);

    const handleStartRegistration = useCallback(
        async (values) => {
            // Trim before deriving the secret: the server builds the secret from the name it receives,
            // so the secret and the sent name must be the same value.
            const studioName = values.studioName.trim();
            const secret = await generateSecretFromObject(studioName);

            // Include all fields in the final account data sent to backend
            const accountData = {
                ...values,
                studioName,
                affiliate,
                secret,
            };
            registerMutation.mutate(accountData);
        },
        [affiliate, registerMutation]
    );

    const handleNavigateHome = useCallback(() => {
        navigate("/");
    }, [navigate]);

    const handleSendSupportTicket = useCallback(
        (formData) => {
            const { email, phone, subject, description, files = [] } = formData;
            supportTicketMutation.mutate({
                email,
                phone,
                subject,
                description,
                files,
            });
        },
        [supportTicketMutation]
    );

    // Auto-navigate to home after 5 seconds on support success
    useEffect(() => {
        if (supportTicketMutation.isSuccess) {
            const timer = setTimeout(() => {
                navigate("/");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [supportTicketMutation.isSuccess, navigate]);

    // Render error state
    if (viewState === VIEW_STATE.REGISTRATION_ERROR) {
        return <SignupErrorView errorText={registrationErrorType} />;
    }

    // Render support success state
    if (viewState === VIEW_STATE.SUPPORT_SUCCESS) {
        return <SignupSupportSuccessView onNavigateHome={handleNavigateHome} />;
    }

    // Render warning state (existing account)
    if (viewState === VIEW_STATE.WARNING) {
        const isSubjectDisabled = Boolean(userReference?.userId);

        return (
            <SignupWarningView
                supportForm={supportForm}
                onSubmit={handleSendSupportTicket}
                onNavigateHome={handleNavigateHome}
                isSubjectDisabled={isSubjectDisabled}
                isPending={supportTicketMutation.isPending}
                isError={supportTicketMutation.isError}
            />
        );
    }

    // Render signup form (default state)
    return (
        <SignupFormView
            form={signupForm}
            onSubmit={handleStartRegistration}
            countrySelection={countrySelection}
            stateSelection={stateSelection}
            onCountryChanged={handleCountryChanged}
            studioNameInUseError={studioNameInUseError}
            onStudioNameBlur={handleStudioNameBlur}
            isSubmitting={registerMutation.isPending}
        />
    );
};

export default Signup;
